import cn, { Argument as ClassName } from 'classnames'
import jstz from 'jstz'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { Message } from 'primereact/message'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import useText from '../../hooks/useText'
import {
    ChargePoint,
    Connector,
    Schedule,
    useScheduleCreateScheduleMutation,
    useScheduleUpdateScheduleMutation,
} from '../../redux/api/web-api'
import { Dialog } from '../../uikit/Dialog'
import { Input } from '../../uikit/Input'
import { Switch } from '../../uikit/Switch'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import Text from '../../../ui/Text'

const formId = 'capi-schedule-form'

const dayFields = [
    { name: 'monday', label: 'lblShortestMonday' },
    { name: 'tuesday', label: 'lblShortestTuesday' },
    { name: 'wednesday', label: 'lblShortestWednesday' },
    { name: 'thursday', label: 'lblShortestThursday' },
    { name: 'friday', label: 'lblShortestFriday' },
    { name: 'saturday', label: 'lblShortestSaturday' },
    { name: 'sunday', label: 'lblShortestSunday' },
] as const

type DayFieldName = (typeof dayFields)[number]['name']

type ScheduleFormValues = Record<DayFieldName, boolean> & {
    name: string
    active: boolean
    startTime: Date
    endTime: Date
    connectorIds: number[]
}

type ScheduleConnector = Connector & { connectorId: number }

type CapiScheduleRequest = Schedule & {
    connectors?: Array<{
        name: string
        connectorId?: number
        affected: boolean
    }>
}

const createTime = (hours = 0, minutes = 0) => {
    const time = new Date(0)
    time.setHours(hours, minutes, 0, 0)
    return time
}

const getFormValues = (schedule?: Schedule): ScheduleFormValues => ({
    name: schedule ? (schedule.name as string) : '',
    active: schedule ? (schedule.active as boolean) : true,
    startTime: createTime(schedule?.startHours, schedule?.startMinutes),
    endTime: createTime(schedule?.endHours, schedule?.endMinutes),
    monday: schedule?.monday ?? false,
    tuesday: schedule?.tuesday ?? false,
    wednesday: schedule?.wednesday ?? false,
    thursday: schedule?.thursday ?? false,
    friday: schedule?.friday ?? false,
    saturday: schedule?.saturday ?? false,
    sunday: schedule?.sunday ?? false,
    connectorIds: schedule ? (schedule.connectorIds as number[]) : [1, 2],
})

export type CapiScheduleDialogProps = {
    className?: ClassName
    visible: boolean
    onHide: () => void
    onSave: () => void
    schedule?: Schedule
    chargePoint: ChargePoint
}

export const CapiScheduleDialog = memo(function CapiScheduleDialog({
    className,
    visible,
    onHide,
    onSave,
    schedule: editableSchedule,
    chargePoint,
}: CapiScheduleDialogProps): React.ReactElement {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()
    const [showInfo, setShowInfo] = useState(false)
    const [saveError, setSaveError] = useState(false)
    const initialValues = useMemo(() => getFormValues(editableSchedule), [editableSchedule])

    const {
        control,
        handleSubmit,
        register,
        reset,
        watch,
        formState: { errors, isSubmitted, isValid },
    } = useForm<ScheduleFormValues>({
        defaultValues: initialValues,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    const [createSchedule, { isLoading: isCreating }] = useScheduleCreateScheduleMutation()
    const [updateSchedule, { isLoading: isUpdating }] = useScheduleUpdateScheduleMutation()
    const isSaving = isCreating || isUpdating
    const scheduleName = watch('name')

    const connectors = [...(chargePoint.connectors ?? [])] as ScheduleConnector[]
    connectors.sort((a, b) => a.connectorId - b.connectorId)

    useEffect(() => {
        if (visible) {
            reset(initialValues)
            setShowInfo(false)
            setSaveError(false)
        }
    }, [initialValues, reset, visible])

    const save: SubmitHandler<ScheduleFormValues> = async (formValues) => {
        setSaveError(false)
        const selectedConnectorIds = connectors
            .filter((connector) => formValues.connectorIds.includes(connector.connectorId))
            .map((connector) => connector.connectorId)

        const schedule: CapiScheduleRequest = {
            ...editableSchedule,
            name: formValues.name,
            active: formValues.active,
            chargePointId: chargePoint.id,
            connectorIdList: selectedConnectorIds.join(','),
            connectorIds: editableSchedule ? editableSchedule.connectorIds : [1, 2],
            connectors: chargePoint.connectors
                ? connectors.map((connector) => ({
                      name: adaptConnectorNaming(connector.name),
                      connectorId: connector.connectorId,
                      affected: formValues.connectorIds.includes(connector.connectorId),
                  }))
                : undefined,
            startHours: formValues.startTime.getHours(),
            startMinutes: formValues.startTime.getMinutes(),
            endHours: formValues.endTime.getHours(),
            endMinutes: formValues.endTime.getMinutes(),
            timeZone: editableSchedule ? editableSchedule.timeZone : jstz.determine().name(),
            monday: formValues.monday,
            tuesday: formValues.tuesday,
            wednesday: formValues.wednesday,
            thursday: formValues.thursday,
            friday: formValues.friday,
            saturday: formValues.saturday,
            sunday: formValues.sunday,
        }

        try {
            if (editableSchedule?.id) await updateSchedule({ schedule }).unwrap()
            else await createSchedule({ schedule }).unwrap()

            onSave()
            onHide()
        } catch {
            setSaveError(true)
        }
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button type="button" text label={text('btnCancel')} onClick={onHide} data-testid="btn-cancel" />
            <Button
                type="submit"
                form={formId}
                label={text('btnSave')}
                loading={isSaving}
                disabled={isSaving || (isSubmitted && !isValid)}
                data-testid="btn-save"
            />
        </div>
    )

    const title = (
        <div className="flex align-items-center gap-1">
            <span>{editableSchedule ? text('lblSchedule', { name: scheduleName }) : text('lblNewScheduleModal')}</span>
            <Button
                type="button"
                text
                rounded
                size="small"
                icon="pi pi-info-circle"
                aria-label={text('lblInfo')}
                aria-expanded={showInfo}
                onClick={() => setShowInfo((isVisible) => !isVisible)}
                data-testid="info-btn"
            />
        </div>
    )

    return (
        <Dialog
            className={cn(className)}
            visible={visible}
            onHide={onHide}
            header={title}
            footer={footer}
            data-testid="capi-schedule-dialog"
        >
            <form id={formId} className="flex flex-column gap-4" onSubmit={handleSubmit(save)} noValidate>
                {showInfo ? (
                    <Message
                        severity="info"
                        className="w-full"
                        data-testid="info-text"
                        content={
                            <Text
                                code="msgScheduleInfo"
                                values={{
                                    test: (
                                        <span className="inline-flex align-items-center gap-1">
                                            <i className="pi pi-toggle-off" />
                                            <i className="pi pi-clock text-red-500" />
                                        </span>
                                    ),
                                }}
                            />
                        }
                    />
                ) : null}

                {saveError ? <Message severity="error" text={text('msgScheduleError')} className="w-full" /> : null}

                <Input
                    id="schedule-name-input"
                    label={text('lblName')}
                    data-testid="name-field"
                    required
                    error={errors.name?.message}
                    {...register('name', { required: text('msgNameRequired') })}
                />

                <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                        <Switch
                            inputId="schedule-active"
                            checked={field.value}
                            onChange={(event) => field.onChange(Boolean(event.value))}
                            label={text(field.value ? 'lblActive' : 'lblInactiveStatus')}
                            data-testid="schedule-active"
                        />
                    )}
                />

                <fieldset className="border-none p-0 m-0">
                    <legend className="p-label mb-2">{text('lblActiveDaysField')}</legend>
                    <div className="flex flex-wrap gap-3">
                        {dayFields.map(({ name, label }) => (
                            <Controller
                                key={name}
                                name={name}
                                control={control}
                                render={({ field }) => (
                                    <div className="flex align-items-center gap-2">
                                        <Checkbox
                                            inputId={`schedule-${name}`}
                                            checked={field.value}
                                            onChange={(event) => field.onChange(Boolean(event.checked))}
                                        />
                                        <label htmlFor={`schedule-${name}`}>{text(label)}</label>
                                    </div>
                                )}
                            />
                        ))}
                    </div>
                </fieldset>

                <div className="flex flex-column sm:flex-row gap-4">
                    <Controller
                        name="startTime"
                        control={control}
                        render={({ field }) => (
                            <div className="flex flex-column gap-2 flex-1">
                                <label className="p-label" htmlFor="schedule-start-time">
                                    {text('lblFromField')}
                                </label>
                                <Calendar
                                    inputId="schedule-start-time"
                                    value={field.value}
                                    onChange={(event) => {
                                        if (event.value instanceof Date) field.onChange(event.value)
                                    }}
                                    timeOnly
                                    hourFormat="24"
                                    showIcon
                                    icon="pi pi-clock"
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="endTime"
                        control={control}
                        render={({ field }) => (
                            <div className="flex flex-column gap-2 flex-1">
                                <label className="p-label" htmlFor="schedule-end-time">
                                    {text('lblToField')}
                                </label>
                                <Calendar
                                    inputId="schedule-end-time"
                                    value={field.value}
                                    onChange={(event) => {
                                        if (event.value instanceof Date) field.onChange(event.value)
                                    }}
                                    timeOnly
                                    hourFormat="24"
                                    showIcon
                                    icon="pi pi-clock"
                                />
                            </div>
                        )}
                    />
                </div>

                <Controller
                    name="connectorIds"
                    control={control}
                    render={({ field }) => (
                        <fieldset className="border-none p-0 m-0">
                            <legend className="p-label mb-2">{text('lblAffectedByScheduleSection')}</legend>
                            <div className="flex flex-column gap-2">
                                {connectors.map((connector) => {
                                    const inputId = `schedule-connector-${connector.connectorId}`
                                    return (
                                        <div key={connector.connectorId} className="flex align-items-center gap-2">
                                            <Checkbox
                                                inputId={inputId}
                                                checked={field.value.includes(connector.connectorId)}
                                                onChange={(event) =>
                                                    field.onChange(
                                                        event.checked
                                                            ? [...field.value, connector.connectorId]
                                                            : field.value.filter((id) => id !== connector.connectorId)
                                                    )
                                                }
                                                data-testid={`connector-${connector.connectorId}`}
                                            />
                                            <label htmlFor={inputId}>{adaptConnectorNaming(connector.name)}</label>
                                        </div>
                                    )
                                })}
                            </div>
                        </fieldset>
                    )}
                />
            </form>
        </Dialog>
    )
})
